import { baseApi } from "../baseApi";
import { pick } from "../util";
import type {
  Customer,
  CreateCustomerInput,
  UpdateCustomerInput,
} from "../types";

const CUSTOMER_FIELDS = `
  id
  organizationId
  name
  code
  email
  phone
  gstin
  contactPerson
  addressLine1
  addressLine2
  city
  state
  country
  postalCode
  creditLimit
  status
  createdAt
  updatedAt
`;

export const customerApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getCustomers: builder.query<Customer[], string | void>({
      query: (organizationId) => ({
        body: {
          query: `
            query Customers($organizationId: String) {
              customers(organizationId: $organizationId) {
                ${CUSTOMER_FIELDS}
              }
            }
          `,
          variables: { organizationId: organizationId ?? null },
        },
      }),

      transformResponse: (res: unknown) => pick<Customer[]>(res, "customers"),

      providesTags: (result) =>
        result
          ? [
              ...result.map((c) => ({ type: "Customer" as const, id: c.id })),
              { type: "Customer" as const, id: "LIST" },
            ]
          : [{ type: "Customer" as const, id: "LIST" }],
    }),

    getCustomer: builder.query<Customer, string>({
      query: (id) => ({
        body: {
          query: `
            query Customer($id: String!) {
              customer(id: $id) {
                ${CUSTOMER_FIELDS}
              }
            }
          `,
          variables: { id },
        },
      }),

      transformResponse: (res: unknown) => pick<Customer>(res, "customer"),

      providesTags: (_result, _err, id) => [{ type: "Customer", id }],
    }),

    createCustomer: builder.mutation<Customer, CreateCustomerInput>({
      query: (input) => ({
        body: {
          query: `
            mutation CreateCustomer($input: CreateCustomerInput!) {
              createCustomer(input: $input) {
                ${CUSTOMER_FIELDS}
              }
            }
          `,
          variables: { input },
        },
      }),

      transformResponse: (res: unknown) => pick<Customer>(res, "createCustomer"),

      invalidatesTags: [{ type: "Customer", id: "LIST" }],
    }),

    updateCustomer: builder.mutation<
      Customer,
      { id: string; input: UpdateCustomerInput }
    >({
      query: ({ id, input }) => ({
        body: {
          query: `
            mutation UpdateCustomer($id: String!, $input: UpdateCustomerInput!) {
              updateCustomer(id: $id, input: $input) {
                ${CUSTOMER_FIELDS}
              }
            }
          `,
          variables: { id, input },
        },
      }),

      transformResponse: (res: unknown) => pick<Customer>(res, "updateCustomer"),

      invalidatesTags: (_result, _err, { id }) => [
        { type: "Customer", id },
        { type: "Customer", id: "LIST" },
      ],
    }),

    deleteCustomer: builder.mutation<boolean, string>({
      query: (id) => ({
        body: {
          query: `
            mutation DeleteCustomer($id: String!) {
              deleteCustomer(id: $id)
            }
          `,
          variables: { id },
        },
      }),

      transformResponse: (res: unknown) => pick<boolean>(res, "deleteCustomer"),

      invalidatesTags: (_result, _err, id) => [
        { type: "Customer", id },
        { type: "Customer", id: "LIST" },
      ],
    }),
  }),
});

export const {
  useGetCustomersQuery,
  useLazyGetCustomersQuery,
  useGetCustomerQuery,
  useLazyGetCustomerQuery,
  useCreateCustomerMutation,
  useUpdateCustomerMutation,
  useDeleteCustomerMutation,
} = customerApi;
